import { Alert, Box, CircularProgress, Tab as MuiTab, Paper, Tabs } from '@mui/material';
import { useState } from 'react';
import { usePermissions, useRecordContext } from 'react-admin';
import { COMMAND_PERMISSIONS } from '../../../constants/Permissions';
import { CommandList } from './CommandList';
import { ExecutionHistory } from './ExecutionHistory';

/**
 * Main Commands tab component for unit detail page
 *
 * Features:
 * - Available commands list
 * - Execution history
 * - Permission checks
 */
export const UnitCommandsTab = () => {
  const record = useRecordContext();
  const { permissions, isLoading } = usePermissions();
  const [activeTab, setActiveTab] = useState(0);

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Check permissions
  const canViewCommands = permissions?.includes(COMMAND_PERMISSIONS.VIEW_COMMANDS) ?? false;
  const canExecuteCommands = permissions?.includes(COMMAND_PERMISSIONS.EXECUTE_COMMANDS) ?? false;
  const canViewExecutions = permissions?.includes(COMMAND_PERMISSIONS.VIEW_EXECUTIONS) ?? false;

  // No permission to view commands at all
  if (!canViewCommands) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          You don't have permission to view unit commands. Please contact your administrator if you need access.
        </Alert>
      </Box>
    );
  }

  if (!record) {
    return null;
  }

  const unitId = Number(record.id);

  return (
    <Box>
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <MuiTab label="Available Commands" />
          {canViewExecutions && <MuiTab label="Execution History" />}
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <CommandList unitId={unitId} canExecute={canExecuteCommands} unitHostname={record.hostname || undefined} />
      )}

      {activeTab === 1 && canViewExecutions && <ExecutionHistory unitId={unitId} />}

      {activeTab === 1 && !canViewExecutions && (
        <Box p={3}>
          <Alert severity="warning">
            You don't have permission to view execution history. Please contact your administrator if you need access.
          </Alert>
        </Box>
      )}
    </Box>
  );
};
